import { type FC } from 'react';

// MUI
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';

// common
import useUserRoles from 'common/hook/useUserRoles';

// screen
import { getAvailableRolesForUser, type OrgTypeApi } from 'modules/account/roleFilters';
import { countCustomers } from 'modules/account/detail/helpers/utils';
import FilterButtons from 'modules/account/components/FilterButtons';
import { CommonRole, Roles } from 'modules/account/services/useGetRoles';
import StaticRoleType from 'modules/account/components/roles/StaticRoleType';
import SystemAdminRoleType from 'modules/account/components/roles/SystemAdminRoleType';
import RoleCategory from 'modules/account/components/RoleCategory';
import { useTabsData } from 'modules/account/detail/hooks/useTabsData';

interface RolesListProps {
  roles: Partial<Roles>;
  onSelectSystemRole: (roleId: string, add: boolean) => void;
  onToggleRoleFromList?: (roleId: string, rolesType: string, checked: boolean) => void;
}

const RolesList: FC<RolesListProps> = ({ roles, onSelectSystemRole, onToggleRoleFromList }) => {
  const {
    userInfo,
    selectedRole,
    viewOnly,
    rolesToAssign,
    isCQQL,
    unitAddress,
    setSelectedRole,
  } = useTabsData();
  const loggedInUserRoles = useUserRoles();
  const orgType = (userInfo?.userAuthen?.userActualManagingOrgType as OrgTypeApi) ?? 'client';
  // lọc vai trò nghiệp vụ theo chức vụ (title) của tài khoản đang xem/sửa — như màn Tạo
  const availableRoles = getAvailableRolesForUser(loggedInUserRoles, orgType, roles ?? {}, userInfo?.title);

  const getIsChecked = (rolesType: string, roleId: string) => {
    if (!rolesToAssign?.rolesAssignment) return false;
    const rta = rolesToAssign.rolesAssignment;
    if (rolesType === 'adminRoles') return countCustomers(rta.forAdminRoles, roleId) > 0;
    if (rolesType === 'businessRoles')
      return isCQQL
        ? !!rta.forGovAgencyBusiness.find((r) => r.roleId === roleId)
        : countCustomers(rta.forClientBusiness, roleId) > 0;
    return false;
  };

  return (
    <>
      <FilterButtons />
      <Box sx={{ marginTop: '32px', marginRight: '16px', paddingBottom: '16px' }}>
        {Object.keys(availableRoles).map((type: keyof Roles) => {
          const rolesInGroup = (availableRoles[type] || []) as CommonRole[];
          if (!rolesInGroup.length) return null;
          const isSystemRole = type === 'systemRoles';
          return (
            <Accordion key={type}>
              <RoleCategory label={`account.roles.${type}`} />
              {rolesInGroup.map((role) => {
                const isStaticRole = role.parentId === null;
                const isCurrentSelected = selectedRole?.role.name === role.name;
                const rolesType = `${type}`;
                return (
                  <AccordionDetails
                    key={role.id}
                    sx={{
                      border: '1px solid',
                      borderColor: isCurrentSelected ? 'primary.main' : 'divider',
                      margin: 1,
                      borderRadius: '12px',
                    }}
                    onClick={() => setSelectedRole({ rolesType, role })}
                  >
                    {isStaticRole &&
                      (isSystemRole ? (
                        <SystemAdminRoleType
                          role={role}
                          onSelectSystemRole={onSelectSystemRole}
                          isChecked={
                            !!rolesToAssign?.rolesAssignment?.forSystemRoles?.find(
                              ({ roleId }) => roleId === role.id
                            )
                          }
                          viewOnly={viewOnly}
                        />
                      ) : (
                        <StaticRoleType
                          role={role}
                          rolesToAssign={rolesToAssign}
                          isCCQL={isCQQL}
                          isCurrentSelected={isCurrentSelected}
                          rolesType={rolesType}
                          unitAddress={unitAddress}
                          isChecked={getIsChecked(rolesType, role.id)}
                          onToggle={
                            onToggleRoleFromList
                              ? (checked) => onToggleRoleFromList(role.id, rolesType, checked)
                              : undefined
                          }
                          viewOnly={viewOnly}
                        />
                      ))}
                  </AccordionDetails>
                );
              })}
            </Accordion>
          );
        })}
      </Box>
    </>
  );
};

export default RolesList;
